import { useCallback } from 'react';
import { TvChannel } from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { useRouter } from '@funtech-int/rival-react';
import { useFeatureFlags, useAuthManager } from '@funtech-int/yango-shared';

import { QueryParameter } from '@/shared/constants/queryParams';
import { PlayerContentType } from '@/scenarios/legacy-player/components/feature-player/types/content';
import { startPlaying } from '@/scenarios/legacy-player/components/player-manager/helpers/startPlaying';
import { useSearchParams } from '@/shared/hooks/useSearchParams';

import { featureFlagsSchema } from '../lib/featureFlags';
import { resolveChannelPlayback } from '../lib/resolveChannelPlayback';

import { useChannelCardModal } from './useChannelCardModal';
import { useNavigateToAuthorizationCallback } from './useNavigateToAuthorizationCallback';

export const useOnChannelCardClick = (channel: TvChannel) => {
    const { isAuthorized } = useAuthManager();
    const channelCardModal = useChannelCardModal();
    const navigateToAuthorization = useNavigateToAuthorizationCallback();
    const router = useRouter();
    const { flags } = useFeatureFlags(featureFlagsSchema);
    const isNewPlayerEnabled = flags.get('isNewPlayerEnabled');
    const searchParams = useSearchParams();

    return useCallback(() => {
        searchParams.set(QueryParameter.OPENED_CHANNEL_ID, channel.contentId);
        router.navigate({
            to: router.location,
            meta: {
                searchParams,
            },
            replace: true,
        });
        resolveChannelPlayback(channel.viewOption, isAuthorized, {
            onNotAvailable: () => {},
            onNeedAuth: () =>
                navigateToAuthorization({
                    [QueryParameter.OPENED_CHANNEL_ID]: channel.contentId,
                }),
            onShowModal: () => channelCardModal.open(channel),
            onPlay: () => {
                startPlaying({
                    router,
                    contentId: channel.contentId,
                    contentType: PlayerContentType.CHANNEL,
                    isNewPlayerEnabled,
                });
            },
        });
    }, [
        router,
        searchParams,
        channel,
        isAuthorized,
        isNewPlayerEnabled,
        navigateToAuthorization,
        channelCardModal,
    ]);
};
